import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import type { ChartOptions, TooltipItem } from 'chart.js';
import type { FreightosData, PromptOverrides } from '@rti/shared';
import { apiClient } from '@rti/shared';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useTheme } from '@/contexts/ThemeContext';
import AIPanel from '@/components/AIPanel';
import { withAlpha } from '@/utils/chartColors';
import { getSmartYAxisConfig } from '@/utils/chartAxisUtils';
import {
  getFreightosData,
  METRIC_KEYS,
  METRIC_LABELS,
  METRIC_ORDER,
  DEFAULT_METRICS,
} from '@/services/freightosService';
import type { MetricKey, TradelaneOption } from '@/services/freightosService';
import { Ship, Plane } from 'lucide-react';

const METRIC_COLORS: Record<MetricKey, string> = {
  maximum: '#fb7185',
  high: '#3b82f6',
  medium_high: '#80ed40',
  medium: '#eab308',
  medium_low: '#f97316',
  low: '#ef4444',
  minimum: '#8b5cf6',
  average: '#a78bfa',
};

type RangePreset = '3M' | '1Y' | 'ALL';

const subtractMonths = (d: Date, months: number) => {
  const r = new Date(d);
  r.setMonth(r.getMonth() - months);
  return r;
};

const fmtDate = (d: Date) => d.toISOString().split('T')[0];

const fmtAxisDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${String(d.getFullYear()).slice(2)}`;
};

const fmtDisplayDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' });
};

const getSortedMetrics = (metrics: Set<MetricKey>): MetricKey[] => {
  return METRIC_KEYS.filter(k => metrics.has(k)).sort((a, b) => (METRIC_ORDER[b] ?? 0) - (METRIC_ORDER[a] ?? 0));
};

const FreightRatesDashboard: React.FC = () => {
  const enabled = useFeatureFlag('freightos-dashboard');
  const { isDark } = useTheme();
  const [rawData, setRawData] = useState<FreightosData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedTradelane, setSelectedTradelane] = useState('');
  const [selectedMode, setSelectedMode] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState<Set<MetricKey>>(new Set(DEFAULT_METRICS));
  const [rangePreset, setRangePreset] = useState<RangePreset>('3M');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [airUnitIsKg, setAirUnitIsKg] = useState(true);
  const [expandMetricsSection, setExpandMetricsSection] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    getFreightosData()
      .then(data => {
        setRawData(data);
        if (data.length > 0) {
          const dates = data.map(d => d.date).sort();
          const maxDate = dates[dates.length - 1];
          const from3m = fmtDate(subtractMonths(new Date(maxDate + 'T00:00:00'), 3));
          setDateFrom(from3m);
          setDateTo(maxDate);
          const first = data[0];
          setSelectedTradelane(first.tradelane);
          setSelectedMode(first.mode);
        }
      })
      .catch(() => setError('Failed to load freight data'))
      .finally(() => setLoading(false));
  }, [enabled]);

  const tradelaneOptions = useMemo((): TradelaneOption[] => {
    const seen = new Map<string, TradelaneOption>();
    rawData.forEach(row => {
      const key = `${row.tradelane}|${row.mode}`;
      if (!seen.has(key)) {
        seen.set(key, { tradelane: row.tradelane, origin: row.origin, destination: row.destination, mode: row.mode });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.tradelane.localeCompare(b.tradelane));
  }, [rawData]);

  const modes = useMemo(() => Array.from(new Set(rawData.map(r => r.mode))).sort(), [rawData]);

  const filteredOptions = useMemo(
    () => tradelaneOptions.filter(o => !selectedMode || o.mode === selectedMode),
    [tradelaneOptions, selectedMode],
  );

  const currentOption = useMemo(
    () => filteredOptions.find(o => o.tradelane === selectedTradelane) ?? filteredOptions[0],
    [filteredOptions, selectedTradelane],
  );

  const filteredRows = useMemo(() => {
    if (!currentOption) return [];
    return rawData
      .filter(r => r.tradelane === currentOption.tradelane && r.mode === currentOption.mode)
      .filter(r => r.date >= dateFrom && r.date <= dateTo)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [rawData, currentOption, dateFrom, dateTo]);

  const applyPreset = useCallback((preset: RangePreset) => {
    setRangePreset(preset);
    const dates = rawData.map(d => d.date).sort();
    if (dates.length === 0) return;
    const maxDate = dates[dates.length - 1];
    const minDate = dates[0];
    setDateTo(maxDate);
    if (preset === '3M') setDateFrom(fmtDate(subtractMonths(new Date(maxDate + 'T00:00:00'), 3)));
    else if (preset === '1Y') setDateFrom(fmtDate(subtractMonths(new Date(maxDate + 'T00:00:00'), 12)));
    else setDateFrom(minDate);
  }, [rawData]);

  const handleModeChange = useCallback((mode: string) => {
    setSelectedMode(mode);
    const available = tradelaneOptions.filter(o => !mode || o.mode === mode);
    if (available.length > 0 && !available.find(o => o.tradelane === selectedTradelane)) {
      setSelectedTradelane(available[0].tradelane);
    }
  }, [tradelaneOptions, selectedTradelane]);

  const toggleMetric = useCallback((key: MetricKey) => {
    setSelectedMetrics(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  }, []);

  // Axis/grid/tooltip colors must derive from the reactive `isDark` value, NOT from
  // getRsColors() (which reads CSS variables once at mount and goes stale on theme change).
  const tickColor = isDark ? '#e9ede6' : '#1e2019';
  const gridColor = isDark ? '#566151' : '#d0d3ce';
  const tooltipBg = isDark ? '#2a2e28' : '#f0f2ee';

  const chartData = useMemo(() => {
    const labels = filteredRows.map(r => r.date);
    const sortedMetrics = getSortedMetrics(selectedMetrics);
    const isAirLb = currentOption?.mode === 'Air' && !airUnitIsKg;
    const conversionFactor = isAirLb ? 2.20462 : 1;
    const datasets = sortedMetrics.map(key => ({
      label: METRIC_LABELS[key],
      data: filteredRows.map(r => {
        const val = r[key] as number | null;
        return val != null ? val / conversionFactor : null;
      }),
      borderColor: METRIC_COLORS[key],
      backgroundColor: withAlpha(METRIC_COLORS[key], 0.05),
      borderWidth: key === 'average' ? 3 : 2,
      pointRadius: rangePreset === '3M' ? 3 : 0,
      pointHoverRadius: 5,
      pointBackgroundColor: METRIC_COLORS[key],
      tension: 0,
      spanGaps: true,
      fill: true,
    }));
    return { labels, datasets };
  }, [filteredRows, selectedMetrics, rangePreset, currentOption?.mode, airUnitIsKg]);

  const allValues = useMemo(() => {
    const isAirLb = currentOption?.mode === 'Air' && !airUnitIsKg;
    const conversionFactor = isAirLb ? 2.20462 : 1;
    return filteredRows.flatMap(r =>
      METRIC_KEYS
        .filter(k => selectedMetrics.has(k))
        .map(k => {
          const val = r[k];
          return typeof val === 'number' ? val / conversionFactor : null;
        })
        .filter((v): v is number => v != null)
    );
  }, [filteredRows, selectedMetrics, currentOption?.mode, airUnitIsKg]);

  const chartOptions = useMemo((): ChartOptions<'line'> => {
    const yConfig = getSmartYAxisConfig(allValues);
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
          backgroundColor: tooltipBg,
          titleColor: tickColor,
          bodyColor: tickColor,
          borderColor: gridColor,
          borderWidth: 1,
          padding: 14,
          bodySpacing: 8,
          boxPadding: 6,
          callbacks: {
            title: (items: TooltipItem<'line'>[]) => {
              if (!items.length) return '';
              const raw = items[0].label;
              const [y, m, d] = raw.split('-').map(Number);
              const formatted = new Date(y, m - 1, d).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
              });
              return formatted;
            },
            label: (item: TooltipItem<'line'>) => {
              const val = item.parsed.y;
              const unitSuffix = currentOption?.mode === 'Air' ? (airUnitIsKg ? '/kg' : '/lb') : '/40ft';
              return `  ${item.dataset.label}    $${val != null ? val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}${unitSuffix}`;
            },
            labelColor: (item: TooltipItem<'line'>) => ({
              borderColor: item.dataset.borderColor as string,
              backgroundColor: item.dataset.borderColor as string,
              borderWidth: 2,
              borderRadius: 4,
            }),
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: tickColor,
            callback: function (_value: string | number, index: number) {
              const label = chartData.labels[index];
              return label ? fmtAxisDate(label) : '';
            },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
          grid: { color: gridColor },
        },
        y: {
          ...yConfig,
          ticks: {
            ...((yConfig as any).ticks || {}),
            color: tickColor,
            callback: (value: string | number) => `$${Number(value).toLocaleString()}`,
          },
          grid: { color: gridColor },
        },
      },
    };
  }, [allValues, chartData.labels, currentOption?.mode, airUnitIsKg, isDark, tickColor, gridColor, tooltipBg]);


  const aiPeriod = rangePreset === '3M' ? '3m' : '1y';


  const fetchAiInsights = useCallback(async () => {
    if (!currentOption) return { brief: null, summary: null, configured: false };
    try {
      return await apiClient.getFreightAIInsights(currentOption.tradelane, currentOption.mode, aiPeriod);
    } catch {
      return { brief: null, summary: null, configured: false };
    }
  }, [currentOption, aiPeriod]);

  const regenerateFreightInsights = useCallback(async (overrides: PromptOverrides) => {
    if (!currentOption) throw new Error('No tradelane selected');
    return await apiClient.regenerateFreightAIInsights(currentOption.tradelane, currentOption.mode, aiPeriod, overrides);
  }, [currentOption, aiPeriod]);

  if (!enabled) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rs-black">
        <p className="text-rs-off-white/60">This feature is not available.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rs-black">
        <div className="animate-pulse text-rs-off-white/60">Loading freight data…</div>
      </div>
    );
  }

  if (error || rawData.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rs-black">
        <p className="text-rs-red">{error || 'No freight data available.'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rs-black">
      <div className="bg-rs-off-black/80 border-b border-rs-jet-light/50 px-6 py-5" style={{ backgroundColor: isDark ? undefined : '#f0f2ee' }}>
        <div className="flex items-center gap-3">
          <Ship className="w-7 h-7 text-rs-green" />
          <h1 className="text-3xl font-light" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>Freight Rates</h1>
        </div>
        <p className="text-sm mt-1" style={{ color: isDark ? 'rgba(243,244,246,0.5)' : 'rgba(30,32,25,0.5)' }}>Global freight rate intelligence powered by Freightos</p>
      </div>

      <div className="p-6 flex flex-col gap-6">
        <div className="bg-rs-off-black/60 backdrop-blur-sm border border-rs-jet-light/50 rounded-xl shadow-lg p-5 flex flex-col gap-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-rs-off-white/60 uppercase tracking-wider mb-1.5">Mode</label>
              <select
                value={selectedMode}
                onChange={e => handleModeChange(e.target.value)}
                className="w-full bg-rs-jet border border-rs-jet-light/50 text-rs-off-white rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-rs-green"
              >
                <option value="">All Modes</option>
                {modes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="flex-[2] min-w-[320px]">
              <label className="block text-xs font-medium text-rs-off-white/60 uppercase tracking-wider mb-1.5">Tradelane</label>
              <select
                value={selectedTradelane}
                onChange={e => setSelectedTradelane(e.target.value)}
                className="w-full bg-rs-jet border border-rs-jet-light/50 text-rs-off-white rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-rs-green"
              >
                {filteredOptions.map(o => (
                  <option key={`${o.tradelane}|${o.mode}`} value={o.tradelane}>
                    {o.tradelane} ({o.origin} → {o.destination})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6 pt-2 border-t border-rs-jet-light/30">
            <div className="flex items-center gap-1">
              {(['3M', '1Y', 'ALL'] as RangePreset[]).map(p => (
                <button
                  key={p}
                  onClick={() => applyPreset(p)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    rangePreset === p
                      ? 'bg-rs-green text-black'
                      : 'bg-rs-jet text-rs-off-white/70 hover:text-rs-off-white'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm text-rs-off-white/70">
              <span className="whitespace-nowrap">From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setRangePreset('ALL'); }}
                className="bg-rs-jet border border-rs-jet-light/50 text-rs-off-white rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-rs-green [color-scheme:dark] min-w-0"
              />
              <span className="whitespace-nowrap">To</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setRangePreset('ALL'); }}
                className="bg-rs-jet border border-rs-jet-light/50 text-rs-off-white rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-rs-green [color-scheme:dark] min-w-0"
              />
            </div>
          </div>
        </div>
        <AIPanel
          fetchFn={fetchAiInsights}
          regenerateFn={regenerateFreightInsights}
          context="freight-rates"
          resetKey={`${currentOption?.tradelane}-${currentOption?.mode}-${rangePreset}`}
          enabled={filteredRows.length > 0}
        />

        {currentOption && (
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-light flex-wrap" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
              {currentOption.tradelane}
              <span className="text-sm sm:text-base ml-2 whitespace-nowrap" style={{ color: isDark ? 'rgba(243,244,246,0.4)' : 'rgba(30,32,25,0.4)' }}>
                ({currentOption.origin} → {currentOption.destination})
              </span>
            </h2>
            <div className="flex flex-col gap-4 items-center mt-4">
              <div className="relative group">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-2 rounded-full text-lg font-medium ${currentOption.mode === 'Air' ? 'bg-rs-purple' : 'bg-rs-blue-dash'}`} style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                  {currentOption.mode === 'Air' ? <Plane size={20} /> : <Ship size={20} />}
                  {currentOption.mode}
                </span>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                  <div className="text-xs whitespace-nowrap rounded-lg px-3 py-2 border" style={{ backgroundColor: isDark ? 'rgba(26,26,26,0.95)' : '#f0f2ee', borderColor: isDark ? 'rgba(61,61,61,0.5)' : '#d0d3ce', color: isDark ? 'rgba(243,244,246,0.8)' : 'rgba(30,32,25,0.8)' }}>
                    {currentOption.mode === 'Air' ? 'Price in USD per kg' : 'Price in USD per 40ft container'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        
        <div className="mb-4 p-4 bg-rs-jet/50 border border-rs-jet-light/30 rounded-lg">
            <button
              onClick={() => setExpandMetricsSection(!expandMetricsSection)}
              className="w-full flex items-center justify-between gap-2 focus:outline-none"
            >
              <span className={`mr-20 text-rs-dark-green transition-transform flex-shrink-0 ${expandMetricsSection ? 'rotate-180' : ''}`}>▼</span>
              <div>
                {currentOption.mode === 'Air' && (
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>Understanding Air Pricing Metrics</h3>
                    <p className="text-sm" style={{ color: isDark ? 'rgba(243,244,246,0.7)' : 'rgba(30,32,25,0.7)' }}>All prices are cost per {airUnitIsKg ? 'kg' : 'lb'}</p>
                  </div>
                )}
                {currentOption.mode === 'FCL' && (
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>Understanding Freight Lane Pricing Metrics</h3>
                    <p className="text-sm" style={{ color: isDark ? 'rgba(243,244,246,0.7)' : 'rgba(30,32,25,0.7)' }}>All prices listed are cost per 40ft Freight Container</p>
                  </div>
                )}
              </div>
              <span className={` ml-20 text-rs-dark-green transition-transform flex-shrink-0 ${expandMetricsSection ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {expandMetricsSection && (
              <div className="mt-4">
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-2.5 uppercase tracking-wider" style={{ color: isDark ? 'rgba(243,244,246,0.9)' : 'rgba(30,32,25,0.9)' }}>What These Metrics Mean</h4>
                  <ul className="flex flex-col gap-2.5 text-sm leading-relaxed" style={{ color: isDark ? 'rgba(243,244,246,0.7)' : 'rgba(30,32,25,0.7)' }}>
                    <li className="flex gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: METRIC_COLORS.maximum }} />
                      <span><strong style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>Maximum (Max)</strong> — The highest price that actually occurred during the observed period. This shows the peak rate experienced in this freight lane.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: METRIC_COLORS.high }} />
                      <span><strong style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>High</strong> — Prices in the upper range of what happened historically. This represents periods when market conditions drove rates significantly above average.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: METRIC_COLORS.average }} />
                      <span><strong style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>Average (Avg)</strong> — The mean price across all data points in the period. This is the mathematical center of historical pricing—what's been paid on average when you total all transactions and divide by volume.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: METRIC_COLORS.medium }} />
                      <span><strong style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>Medium (Med)</strong> — The typical or most common price range seen. This represents the middle point of the price distribution and is often different from average, as it's less affected by extreme highs or lows.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: METRIC_COLORS.low }} />
                      <span><strong style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>Low</strong> — Prices in the lower range of historical data. This shows when market conditions were favorable and rates dropped below typical levels.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: METRIC_COLORS.minimum }} />
                      <span><strong style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>Minimum (Min)</strong> — The lowest price that actually occurred. This represents the best rate achieved in this lane historically.</span>
                    </li>
                  </ul>
                </div>
                <div className="mt-4">
                  <h4 className="text-sm font-medium uppercase tracking-wider mb-1" style={{ color: isDark ? 'rgba(243,244,246,0.9)' : 'rgba(30,32,25,0.9)' }}>Why These Bands Matter</h4>
                  <p className="text-sm leading-relaxed mb-3" style={{ color: isDark ? 'rgba(243,244,246,0.7)' : 'rgba(30,32,25,0.7)' }}>These tiered levels show the historical price spectrum. The Average tells you what has actually been paid overall, while Medium shows what typically happens. Together they help identify if pricing has been skewed by occasional spikes or dips.</p>
                </div>
              </div>
            )}
          </div>
        <div className="bg-rs-off-black/60 backdrop-blur-sm border border-rs-jet-light/50 rounded-xl shadow-lg p-5">
          {filteredRows.length > 0 && (
            (() => {
              const sortedMetrics = getSortedMetrics(selectedMetrics);
              const isAirLb = currentOption?.mode === 'Air' && !airUnitIsKg;
              const conversionFactor = isAirLb ? 2.20462 : 1;
              const count = sortedMetrics.length;
              let gridCols = 'grid-cols-3';
              if (count <= 3) gridCols = 'grid-cols-3';
              else if (count === 4) gridCols = 'grid-cols-4';
              else if (count === 5) gridCols = 'grid-cols-5';
              else if (count === 6) gridCols = 'grid-cols-6';
              else if (count === 7) gridCols = 'grid-cols-7';
              else gridCols = 'grid-cols-8';
              return (
                <div className="flex justify-center mb-4">
                  <div className={`grid ${gridCols} gap-3 w-fit`}>
                    {sortedMetrics.map(key => {
                      const latest = filteredRows[filteredRows.length - 1];
                      const rawVal = latest[key] as number | null;
                      const val = rawVal != null ? rawVal / conversionFactor : null;
                      const prev = filteredRows.length > 1 ? (filteredRows[filteredRows.length - 2][key] as number | null) : null;
                      const prevConverted = prev != null ? prev / conversionFactor : null;
                      const delta = val != null && prevConverted != null ? val - prevConverted : null;
                      return (
                        <div key={key} className="border rounded-lg p-3 text-center" style={{ backgroundColor: isDark ? 'rgba(30,32,25,0.6)' : '#f0f2ee', borderColor: isDark ? 'rgba(61,61,61,0.3)' : '#d0d3ce' }}>
                          <div className="flex items-center justify-center gap-1.5 mb-1">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: METRIC_COLORS[key] }} />
                            <span className="text-[11px] uppercase tracking-wider" style={{ color: isDark ? 'rgba(243,244,246,0.5)' : 'rgba(30,32,25,0.5)' }}>{METRIC_LABELS[key]}</span>
                          </div>
                          <div className="text-lg font-semibold" style={{ color: isDark ? '#f3f4f6' : '#1e2019' }}>
                            {val != null ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </div>
                          {delta != null && (
                            <div className={`text-xs mt-0.5 ${delta >= 0 ? 'text-rs-green' : 'text-rs-red'}`}>
                              {delta >= 0 ? '▲' : '▼'} ${Math.abs(delta).toFixed(2)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()
          )}
          {currentOption.mode === 'Air' && (
            <div className="flex justify-center mb-4">
              <button
                onClick={() => setAirUnitIsKg(!airUnitIsKg)}
                className="green"
              >
                Switch to {airUnitIsKg ? 'Price per lb' : 'Price per kg'}
              </button>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mb-4">
            {METRIC_KEYS.slice().sort((a, b) => (METRIC_ORDER[b] ?? 0) - (METRIC_ORDER[a] ?? 0)).map(key => {
              const active = selectedMetrics.has(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleMetric(key)}
                  className="flex items-center gap-2 group focus:outline-none"
                  title={active ? `Hide ${METRIC_LABELS[key]}` : `Show ${METRIC_LABELS[key]}`}
                >
                  <span className="relative flex items-center" style={{ width: 28, height: 14 }}>
                    <span
                      className="absolute inset-x-0 top-1/2 -translate-y-1/2 rounded-sm transition-opacity"
                      style={{ height: key === 'average' ? 3 : 2, backgroundColor: METRIC_COLORS[key], opacity: active ? 1 : 0.25 }}
                    />
                    {rangePreset === '3M' && (
                      <span
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-opacity"
                        style={{ width: 6, height: 6, backgroundColor: METRIC_COLORS[key], opacity: active ? 1 : 0.25 }}
                      />
                    )}
                  </span>
                  <span
                    className="text-sm transition-colors"
                    style={{ color: active ? (isDark ? '#f3f4f6' : '#1e2019') : (isDark ? 'rgba(243,244,246,0.35)' : 'rgba(30,32,25,0.35)'), textDecoration: active ? 'none' : 'line-through' }}
                  >
                    {METRIC_LABELS[key]}
                  </span>
                </button>
              );
            })}
          </div>

          {filteredRows.length === 0 ? (
            <div className="flex items-center justify-center h-80" style={{ color: isDark ? 'rgba(243,244,246,0.4)' : 'rgba(30,32,25,0.4)' }}>
              No data for the selected filters and date range.
            </div>
          ) : (
            <div className="h-[320px] sm:h-[420px] md:h-[480px]">
              <Line data={chartData} options={chartOptions} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreightRatesDashboard;
